import { useTranslation } from "react-i18next";
import { Bed, Moon, Sun } from "lucide-react";
import { Card, Progress, Skeleton, Typography } from "@akuna/ui";
import { useHotelRooms } from "@/hooks/useHotelRooms";
import type { StayShowFilter } from "../schedule/scheduleModel";
import type { DaycareOccupancy, SizeOccupancy } from "./panelModel";

const SIZE_META: Record<SizeOccupancy["size"], { dot: string; labelKey: string }> = {
  small: { dot: "bg-akuna-blue", labelKey: "panel.sizeSmall" },
  medium: { dot: "bg-akuna-amber", labelKey: "panel.sizeMedium" },
  large: { dot: "bg-akuna-purple", labelKey: "panel.sizeLarge" },
};

function OccupancyHeader({
  icon,
  iconClass,
  title,
  subtitle,
  used,
  totalLabel,
}: {
  icon: React.ReactNode;
  iconClass: string;
  title: string;
  subtitle: string;
  used: number;
  totalLabel: string;
}) {
  return (
    <div className="mb-3.5 flex items-center gap-2.5">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconClass}`}>{icon}</div>
      <div className="min-w-0">
        <Typography variant="h5" className="truncate">{title}</Typography>
        <Typography variant="caption" className="truncate text-muted-foreground">{subtitle}</Typography>
      </div>
      <div className="ml-auto flex shrink-0 items-baseline gap-1.5">
        <Typography asChild variant="h2"><span>{used}</span></Typography>
        <Typography variant="body-sm" className="text-muted-foreground">/ {totalLabel}</Typography>
      </div>
    </div>
  );
}

export function OccupancyCards({ occupancy, show }: { occupancy: DaycareOccupancy; show: StayShowFilter }) {
  const { t } = useTranslation("daycareHotel");
  const rooms = useHotelRooms();

  const hotelBeds = (rooms.data ?? []).reduce(
    (totals, room) => ({
      used: totals.used + room.occupancy,
      total: totals.total + (room.capacity ?? room.beds.length),
    }),
    { used: 0, total: 0 },
  );

  return (
    <div className="mb-6 grid gap-4 md:grid-cols-2">
      {show !== "hotel" && (
        <Card className="p-5">
          <OccupancyHeader
            icon={<Sun aria-hidden="true" size={20} />}
            iconClass="bg-orange-50 text-akuna-orange"
            title={t("panel.daycareTitle")}
            subtitle={t("panel.daycareSubtitle")}
            used={occupancy.used}
            totalLabel={occupancy.total == null ? t("panel.noCapacity") : String(occupancy.total)}
          />
          <Progress
            className="mb-4"
            label={t("panel.daycareOccupancyLabel")}
            max={occupancy.total ?? Math.max(occupancy.used, 1)}
            value={occupancy.used}
          />
          <div className="grid grid-cols-3 gap-2.5">
            {occupancy.bySize.map((bucket) => (
              <div key={bucket.size} className="rounded-xl border border-border px-3 py-2.5">
                <div className="mb-1 flex items-center gap-1.5">
                  <span aria-hidden="true" className={`h-2.5 w-2.5 shrink-0 rounded-full ${SIZE_META[bucket.size].dot}`} />
                  <Typography variant="caption" className="truncate text-muted-foreground">
                    {t(SIZE_META[bucket.size].labelKey)}
                  </Typography>
                </div>
                <Typography variant="h5">
                  {bucket.capacity == null ? bucket.used : `${bucket.used} / ${bucket.capacity}`}
                </Typography>
              </div>
            ))}
          </div>
        </Card>
      )}

      {show !== "daycare" && (
        rooms.isLoading ? (
          <Skeleton className="h-44 rounded-2xl" label={t("common.loading")} />
        ) : (
          <Card className="p-5">
            <OccupancyHeader
              icon={<Moon aria-hidden="true" size={20} />}
              iconClass="bg-purple-50 text-akuna-purple"
              title={t("panel.hotelTitle")}
              subtitle={t("panel.hotelSubtitle")}
              used={hotelBeds.used}
              totalLabel={String(hotelBeds.total)}
            />
            <Progress
              className="mb-4"
              label={t("panel.hotelOccupancyLabel")}
              max={Math.max(hotelBeds.total, 1)}
              value={hotelBeds.used}
            />
            <ul className="flex flex-col gap-2">
              {(rooms.data ?? []).map((room) => (
                <li key={room.id} className="flex items-center gap-2">
                  <Bed aria-hidden="true" size={16} className="shrink-0 text-akuna-purple" />
                  <Typography variant="body-sm" className="truncate text-neutral-600">
                    {t("panel.roomOccupancy", {
                      name: room.name,
                      used: room.occupancy,
                      total: room.capacity ?? room.beds.length,
                    })}
                  </Typography>
                </li>
              ))}
            </ul>
          </Card>
        )
      )}
    </div>
  );
}
